<template>
  <el-row :gutter="16">
    <el-col :span="12">
      <PDatePicker
        type="date"
        v-model="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 2px">
      <PSelect
        v-model="empresa"
        placeholder="Selecione um tipo de arquivo"
        label="Tipo de arquivo"
        :options="empresasOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin-top: 2px">
      <PSelect
        v-model="tipoDePesquisa"
        placeholder="Selecione um tipo de pesquisa"
        label="Tipo de pesquisa"
        :options="TipoDePesquisaPagHiperOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox label="Selecionar um período" v-model="selecionarPeriodo" />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="inicio"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de início"
        label="Início"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="fim"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { DITypes } from "@/core/@types/types";
import {
  ETipoDePesquisaPagHiper,
  TipoDePesquisaPagHiperOpt
} from "../../../../../../@types/IntegracoesAPIEnum";
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import { useForm } from "vee-validate";
import { computed, onMounted } from "vue";
import {
  formPagHiperSchema,
  IFormularioPagHiperSchema
} from "./formularioPagHiperSchema";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioPagHiperSchema>({
    validationSchema: formPagHiperSchema,
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      empresa: "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaPagHiper.None.toString()
    }
  });

const [date] = defineField("date");
const [empresa] = defineField("empresa");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");
const [tipoDePesquisa] = defineField("tipoDePesquisa");

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";
  const missingTipoDePesquisa =
    !tipoDePesquisa.value || String(tipoDePesquisa.value).trim() === "";

  if (missingDate || missingTipoDePesquisa) return true;

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  if (!financasFeatureGateway) return;

  const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
    props.webservice?.id ?? ""
  );

  if (response?.Ok) {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: response.Result,
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaPagHiper.None.toString()
    });
  }
};

onMounted(async () => {
  if (props.apiSelecionada?.requiresCompanyLookup) {
    try {
      await obterEmpresaPorConfiguracao();
    } catch (error) {
      console.error(error);
    }
  } else {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: props.webservice?.empresa ?? "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoDePesquisa: ETipoDePesquisaPagHiper.None.toString()
    });
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit
});
</script>

<style lang="scss" scoped></style>
